import { HttpClient } from '@angular/common/http';
import { ItemProfile, ItemResource, ItemSchema, Link, MimeTypeEnum, ResourceAlps } from '@ngx-hal/models';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export class ItemProfileWebService<Entity extends ItemResource> {
  constructor(private readonly httpClient: HttpClient) {}

  /**
   * Retrieves the item profile for a given resource link by fetching both its ALPS and schema representations.
   *
   * This method performs two parallel HTTP GET requests:
   * - One to obtain the ALPS (Application-Level Profile Semantics) document describing the resource.
   * - Another to obtain the JSON schema of the resource.
   *
   * Both requests use the appropriate `Accept` headers and query parameters to specify the desired response format.
   * The results are combined into a single observable emitting an object containing the ALPS descriptor and the schema.
   *
   * @param link The resource link for which to retrieve the item profile.
   * @returns An Observable emitting an object with the ALPS descriptor and the resource schema.
   */
  read(link: Link): Observable<ItemProfile<Entity>> {
    return forkJoin([
      this.httpClient.get<{ alps: ResourceAlps<Entity> }>(`${link.href}?accept=${MimeTypeEnum.ALPS}`, {
        headers: { Accept: MimeTypeEnum.ALPS },
      }),
      this.httpClient.get<ItemSchema<Entity>>(`${link.href}?accept=${MimeTypeEnum.Schema}`, {
        headers: { Accept: MimeTypeEnum.Schema },
      }),
    ]).pipe(map(([document, schema]) => ({ alps: document.alps.descriptor[0], schema })));
  }
}
